import { Suspense, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonate } from '@/contexts/ImpersonateContext';
import { useLicense } from '@/hooks/useLicense';
import { useFeatureToggles, type FeatureKey } from '@/hooks/useFeatureToggles';
import { useTenantModules as useTenantModulesHook, type ModuleKey } from '@/hooks/useTenantModules';
import { useMyPermissions } from '@/hooks/usePermissions';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLayout from '@/components/AppLayout';
import UserMenu from '@/components/UserMenu';
import ImpersonateBanner from '@/components/ImpersonateBanner';
import UniverseProvider from '@/components/UniverseProvider';
import posVisionIcon from '@/assets/posvision-logo.png';
import { LoadingSpinner } from './LoadingSpinner';
import { RoutePermissionGuard } from './RoutePermissionGuard';
import { PrintFailureBanner } from '@/features/printing';
import { PdaRouteGuard } from '@/features/pda';
import { FormationChecklistCard } from '@/features/formation';
import { LicenseActivation, UserHub } from './lazyPages';

/** Domaines exemptés de licence machine (VisionPos + LinkVision) */
const LICENSE_EXEMPT_DOMAINS = ['visionpos.fr', 'myposvision.fr', 'linkvision.fr'];

function isLicenseExempt(email: string | undefined): boolean {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return LICENSE_EXEMPT_DOMAINS.some((d) => domain === d || domain?.endsWith('.' + d));
}

/**
 * LicenseGuard — vérifie qu'une licence machine est active.
 * Exemptions : super_admin, comptes @visionpos.fr, @linkvision.fr
 * Si pas de licence → redirige vers la page d'activation.
 */
function LicenseGuard({ children }: { children: ReactNode }) {
  const { user, userRole } = useAuth();
  const { isValid } = useLicense();

  // Exemptions
  if (userRole === 'super_admin') return <>{children}</>;
  if (isLicenseExempt(user?.email)) return <>{children}</>;
  if (isValid) return <>{children}</>;

  // Pas de licence valide → page d'activation
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <LicenseActivation />
    </Suspense>
  );
}

function SuperAdminGuard({ children }: { children: ReactNode }) {
  const { userRole } = useAuth();
  const { isImpersonating } = useImpersonate();
  const location = useLocation();

  // Super admin can access all routes — no forced redirect to admin-portal
  // The admin-portal is still accessible directly via /admin-portal
  if (userRole === 'super_admin' && !isImpersonating) {
    // Only redirect to admin-portal from the dashboard (entry point)
    if (location.pathname === '/dashboard') {
      return <Navigate to="/admin-portal" replace />;
    }
  }
  return <>{children}</>;
}

/**
 * ModuleGuard — vérifie qu'au moins un des modules requis est activé.
 * Si aucun module actif, redirige vers /dashboard.
 * Les super_admin passent toujours.
 */
export function ModuleGuard({ modules, children }: { modules: ModuleKey[]; children: ReactNode }) {
  const { isEnabled } = useTenantModulesHook();
  const { userRole } = useAuth();

  // Super admin always passes
  if (userRole === 'super_admin') return <>{children}</>;

  // Check if at least one required module is enabled
  const hasAccess = modules.length === 0 || modules.some((m) => isEnabled(m));
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * FeatureGuard — vérifie qu'un feature toggle tenant est activé.
 * Si désactivé, redirige vers redirectTo (défaut /dashboard).
 * Les super_admin passent toujours. Pendant le chargement des toggles,
 * computeFeatureEnabled applique le défaut du flag (TRUE hors opt-in) → pas de flash.
 */
export function FeatureGuard({
  feature,
  redirectTo = '/dashboard',
  children,
}: {
  feature: FeatureKey;
  redirectTo?: string;
  children: ReactNode;
}) {
  const { isFeatureEnabled } = useFeatureToggles();
  const { userRole } = useAuth();

  if (userRole === 'super_admin') return <>{children}</>;
  if (!isFeatureEnabled(feature)) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

/**
 * DashboardPermissionGuard — /dashboard (Accueil KPI) suit la permission
 * `reports` (cf permissionFilter.ts). Un rôle sans cette permission voit à la
 * place UserHub (page menu neutre, barre latérale conservée) : la caisse plein
 * écran revient sur /dashboard pour rejoindre le menu, une redirection vers la
 * caisse bouclerait. Admins : passent. Échec RPC (loaded=false) : dashboard.
 */
/** Accueil : checklist de prise en main (flag `formation_checklist`) au-dessus du tableau de bord. */
function WithFormation({ children }: { children: ReactNode }) {
  return (
    <>
      <FormationChecklistCard className="mx-4 mt-4 sm:mx-6" />
      {children}
    </>
  );
}

export function DashboardPermissionGuard({ children }: { children: ReactNode }) {
  const { userRole } = useAuth();
  const { can, loading, loaded } = useMyPermissions();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  if (isAdmin) return <WithFormation>{children}</WithFormation>;
  if (loading) return <LoadingSpinner />;
  if (loaded && !can('reports')) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <UserHub />
      </Suspense>
    );
  }
  return <WithFormation>{children}</WithFormation>;
}

/** Contenu commun à toutes les pages protégées : garde de permissions + bons non imprimés. */
function PageBody({ children }: { children: ReactNode }) {
  return (
    <>
      <PdaRouteGuard>
        <RoutePermissionGuard>{children}</RoutePermissionGuard>
      </PdaRouteGuard>
      <PrintFailureBanner />
    </>
  );
}

/** Wrapper standard : route protégée + licence + redirection super_admin + layout app. */
export function P({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <LicenseGuard>
        <SuperAdminGuard>
          <UniverseProvider>
            <AppLayout>
              <PageBody>{children}</PageBody>
            </AppLayout>
          </UniverseProvider>
        </SuperAdminGuard>
      </LicenseGuard>
    </ProtectedRoute>
  );
}

/** Wrapper plein écran AVEC header minimal (impersonation, logo, menu). */
export function PFull({ children }: { children: ReactNode }) {
  const { isImpersonating, impersonatedName } = useImpersonate();
  return (
    <ProtectedRoute>
      <LicenseGuard>
        <UniverseProvider>
          <div className="flex min-h-screen w-full flex-col bg-background">
            {isImpersonating && <ImpersonateBanner />}
            <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-card px-4">
              <a href="/dashboard" className="hover:opacity-80">
                <img src={posVisionIcon} alt="VisionPos 360" className="h-7 w-auto" />
              </a>
              <div className="flex items-center gap-3">
                {isImpersonating && (
                  <span className="flex items-center gap-1 text-sm font-medium text-amber-600">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                    {impersonatedName}
                  </span>
                )}
                <UserMenu />
              </div>
            </header>
            <main className="flex-1 overflow-auto">
              <PageBody>{children}</PageBody>
            </main>
          </div>
        </UniverseProvider>
      </LicenseGuard>
    </ProtectedRoute>
  );
}

/** Wrapper plein écran SANS header — pour CaisseRapide, Kiosque, etc. */
export function PNoHeader({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <LicenseGuard>
        <UniverseProvider>
          <PageBody>{children}</PageBody>
        </UniverseProvider>
      </LicenseGuard>
    </ProtectedRoute>
  );
}
